import { useMediaQueryList } from "@hooks/use-media-query";
import { Box, Divider, Stack, Step, StepLabel } from "@mui/material";
import { DataManagementStepIcon } from "@pages/data-management/data-management-step-icon";
import { StepState } from "@pages/data-management/data-management-types";
import { sphereColors } from "@styles/common-colors";

interface Props {
  /** State of the step. */
  stepState: StepState;
  /** The title caption to show when the step is active. */
  titleActive: string;
  /** The title caption to show when the step is another state. */
  titleOther: string;
  /** Flag whether this is the last step and no following line should be drawn. */
  isLastStep?: boolean;
}

/** A single step for the progress stepper in Data Management. */
export function DataManagementStep({ stepState, titleActive, titleOther, isLastStep }: Props): JSX.Element {
  const { isExtraSmall } = useMediaQueryList();

  let title: string;
  let subtitle: string;
  let colorTitle: string;
  let colorSubtitle: string;

  // Setting !important is necessary since MUI also sets the default style by a different, generated selector path.
  switch (stepState) {
    case "todo":
      colorTitle = `${sphereColors.gray500} !important`;
      colorSubtitle = sphereColors.gray500;
      subtitle = "Pending";
      title = titleOther;
      break;
    case "active":
      colorTitle = `${sphereColors.blue500} !important`;
      colorSubtitle = sphereColors.gray800;
      subtitle = "In progress";
      title = titleActive;
      break;
    case "error":
      colorTitle = `${sphereColors.gray800} !important`;
      colorSubtitle = sphereColors.red500;
      subtitle = "Failed";
      title = titleOther;
      break;
    case "done":
      colorTitle = `${sphereColors.gray800} !important`;
      colorSubtitle = sphereColors.blue500;
      subtitle = "Complete";
      title = titleOther;
      break;
  }

  if (isExtraSmall) {
    title = "";
    subtitle = "";
  }

  return (
    <Stack direction="column" spacing={0} sx={{ width: isLastStep ? undefined : "100%" }}>
      <Stack direction="row" spacing={0} sx={{ alignItems: "center" }}>
        <Step
          active={stepState === "active"}
          completed={stepState === "done"}
        >
          <StepLabel
            StepIconComponent={DataManagementStepIcon}
            error={stepState === "error"}
            sx={{
              // Setting !important is necessary since MUI also sets the default style by a different, generated selector path.
              textTransform: "uppercase",
              "& .MuiStepLabel-label": {
                color: colorTitle,
                fontWeight: "600 !important",
              },
              "& .Mui-active": {
                color: colorTitle,
                fontWeight: "600 !important",
              },
            }}
          >
            {title}
          </StepLabel>
        </Step>
        { !isLastStep && (
          <Divider sx={{
            flex: "1 1 auto",
            marginLeft: "10px",
            marginRight: "10px",
          }}/>
        )}
      </Stack>

      <Box sx={{
        width: "fit-content",
        marginLeft: "32px",
        marginTop: "4px",
        color: colorSubtitle,
      }}>
        { subtitle }
      </Box>
    </Stack>
  );
}
